import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, CalendarDays, Trophy, History, Loader2, Coins } from "lucide-react";
import { useAuthStore } from "@/features/auth/auth-store";
import { apiGetMe } from "@/features/auth/api";
import {
  apiStartDaily,
  apiAnswerDaily,
  apiGetDailyRankings,
  apiGetDailyHistory,
} from "./api";
import { PreflopRound } from "./PreflopRound";
import { RoundFeedback } from "./RoundFeedback";
import { SessionResults } from "./SessionResults";
import { showPreflopInterstitialAd, resetInterstitialCounter } from "@/lib/admob";
import type {
  PreflopAction,
  RoundData,
  AnswerResponse,
  RoundResult,
  DailyRankings,
  DailyHistoryEntry,
} from "./types";

type Phase = "hub" | "playing" | "feedback" | "results";

export const PreflopGame = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const authToken = useAuthStore((s) => s.token);
  const updateUser = useAuthStore((s) => s.updateUser);

  const [phase, setPhase] = useState<Phase>("hub");
  const [loading, setLoading] = useState(false);

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentRound, setCurrentRound] = useState<RoundData | null>(null);
  const [roundNumber, setRoundNumber] = useState(1);
  const [totalRounds, setTotalRounds] = useState(10);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  // Feedback state
  const [lastResult, setLastResult] = useState<AnswerResponse | null>(null);

  // Track daily mode answers for correct results display
  const dailyCorrectRef = useRef(0);
  const dailyRoundsRef = useRef<RoundResult[]>([]);

  // Results state
  const [finalResults, setFinalResults] = useState<{
    score: number;
    correct: number;
    total: number;
    bestStreak: number;
    chipsEarned: number;
    rounds: RoundResult[];
  } | null>(null);

  // Hub state
  const [rankings, setRankings] = useState<DailyRankings | null>(null);
  const [history, setHistory] = useState<DailyHistoryEntry[]>([]);
  const [hubLoading, setHubLoading] = useState(true);
  const [dailyCompleted, setDailyCompleted] = useState(false);

  // Load hub data
  useEffect(() => {
    if (!authToken) return;
    setHubLoading(true);

    Promise.all([
      apiGetDailyRankings(authToken).catch(() => null),
      apiGetDailyHistory(authToken).catch(() => ({ history: [] })),
    ]).then(([rankingsData, historyData]) => {
      if (rankingsData) {
        setRankings(rankingsData);
        if (rankingsData.yourScore !== null) setDailyCompleted(true);
      }
      setHistory(historyData?.history ?? []);
    }).finally(() => setHubLoading(false));
  }, [authToken, phase]);

  const handleStartDaily = async (dateStr?: string) => {
    if (!authToken) return;
    setLoading(true);
    dailyCorrectRef.current = 0;
    dailyRoundsRef.current = [];
    resetInterstitialCounter();
    try {
      const res = await apiStartDaily(authToken, dateStr);
      setSessionId(res.sessionId);
      setCurrentRound(res.round);
      setRoundNumber(1);
      setTotalRounds(res.totalRounds);
      setScore(0);
      setStreak(0);
      setPhase("playing");
    } catch (err) {
      console.error("Failed to start daily:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (action: PreflopAction) => {
    if (!authToken || !sessionId) return;
    setLoading(true);
    try {
      const res = await apiAnswerDaily(authToken, sessionId, action);
      setLastResult(res);
      setScore(res.score);
      setStreak(res.streak);
      setPhase("feedback");

      // Track round data for results
      if (currentRound) {
        if (res.correct) dailyCorrectRef.current += 1;
        dailyRoundsRef.current.push({
          hand: currentRound.hand,
          handNotation: "",
          position: currentRound.position,
          yourAction: action,
          correctAction: res.correctAction,
          correct: res.correct,
          explanationEn: res.explanationEn,
          explanationEs: res.explanationEs,
        });
      }

      if (res.finished) {
        const correctSoFar = dailyCorrectRef.current + (res.correct ? 1 : 0);
        setFinalResults({
          score: res.score,
          correct: correctSoFar,
          total: res.totalRounds,
          bestStreak: res.bestStreak,
          chipsEarned: res.chipsEarned ?? 0,
          rounds: dailyRoundsRef.current,
        });

        // Refresh chips
        apiGetMe(authToken)
          .then((me) => updateUser({ chips: me.chips }))
          .catch(() => {});
      }
    } catch (err) {
      console.error("Failed to submit answer:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAfterFeedback = useCallback(async () => {
    if (!lastResult) return;

    if (lastResult.finished && finalResults) {
      setPhase("results");
    } else if (lastResult.nextRound) {
      await showPreflopInterstitialAd();
      setCurrentRound(lastResult.nextRound);
      setRoundNumber(lastResult.roundNumber + 1);
      setPhase("playing");
    }
  }, [lastResult, finalResults]);

  const handlePlayAgain = () => {
    setPhase("hub");
    setSessionId(null);
    setCurrentRound(null);
    setLastResult(null);
    setFinalResults(null);
  };

  // --- Hub view ---
  if (phase === "hub") {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3 pt-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/minigames")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{t('preflop_title')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('preflop_subtitle')}
              </p>
            </div>
          </div>

          <Tabs defaultValue="daily">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="daily" className="flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" />
                {t('preflop_tab_daily')}
              </TabsTrigger>
              <TabsTrigger value="rankings" className="flex items-center gap-1.5">
                <Trophy className="h-4 w-4" />
                {t('preflop_tab_rankings')}
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-1.5">
                <History className="h-4 w-4" />
                {t('preflop_tab_history')}
              </TabsTrigger>
            </TabsList>

            {/* Daily tab */}
            <TabsContent value="daily" className="mt-4 space-y-4">
              <Card className="p-5 text-center space-y-3">
                <CalendarDays className="h-10 w-10 text-primary mx-auto" />
                <h2 className="text-xl font-bold">{t('preflop_daily_title')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t('preflop_daily_desc')}
                </p>
                <div className="flex items-center justify-center gap-1 text-sm">
                  <Coins className="h-4 w-4 text-secondary" />
                  <span className="text-secondary font-semibold">+20</span>
                  <span className="text-muted-foreground">{t('preflop_daily_reward')}</span>
                </div>
                {dailyCompleted ? (
                  <div className="space-y-2">
                    <Badge variant="secondary" className="text-sm py-1 px-3">
                      {t('preflop_daily_completed')}
                    </Badge>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full"
                      onClick={() => handleStartDaily()}
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('preflop_daily_replay')}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="lg"
                    className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    onClick={() => handleStartDaily()}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('preflop_daily_start')}
                  </Button>
                )}
              </Card>
            </TabsContent>

            {/* Rankings tab */}
            <TabsContent value="rankings" className="mt-4 space-y-4">
              {hubLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : rankings ? (
                <>
                  {rankings.yourRank !== null && (
                    <Card className="p-4 text-center bg-primary/5 border-primary/20">
                      <p className="text-sm text-muted-foreground">{t('preflop_your_rank')}</p>
                      <p className="text-3xl font-bold text-primary">#{rankings.yourRank}</p>
                      <p className="text-sm text-muted-foreground">
                        Score: {rankings.yourScore} · Top {rankings.percentile !== null ? `${100 - rankings.percentile}%` : "—"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {rankings.totalPlayers} {t('preflop_players_today')}
                      </p>
                    </Card>
                  )}

                  {rankings.topPlayers.length > 0 ? (
                    <Card className="p-0 overflow-hidden">
                      <div className="p-3 border-b border-white/10">
                        <h3 className="font-bold text-sm">{t('preflop_leaderboard')}</h3>
                      </div>
                      {rankings.topPlayers.map((p) => (
                        <div key={p.rank} className="flex items-center justify-between p-3 border-b border-white/5 last:border-0">
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-bold w-6 text-center ${p.rank <= 3 ? "text-secondary" : "text-muted-foreground"}`}>
                              {p.rank}
                            </span>
                            <span className="font-semibold">{p.username}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{p.correct}/10</span>
                            <Badge variant="secondary" className="text-xs">{p.score}</Badge>
                          </div>
                        </div>
                      ))}
                    </Card>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      {t('preflop_no_rankings')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('preflop_play_to_rank')}
                </p>
              )}
            </TabsContent>

            {/* History tab */}
            <TabsContent value="history" className="mt-4 space-y-3">
              {hubLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : history.length > 0 ? (
                history.map((entry) => (
                  <Card key={entry.date} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{entry.date}</p>
                      <p className="text-sm text-muted-foreground">
                        {entry.correct}/{entry.total} {t('preflop_correct')}
                      </p>
                    </div>
                    <Badge variant="secondary">{entry.score} {t('preflop_pts')}</Badge>
                  </Card>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t('preflop_no_history')}
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  if (phase === "playing" && currentRound) {
    return (
      <PreflopRound
        round={currentRound}
        roundNumber={roundNumber}
        totalRounds={totalRounds}
        score={score}
        streak={streak}
        onAnswer={handleAnswer}
        disabled={loading}
      />
    );
  }

  if (phase === "feedback" && lastResult) {
    return (
      <>
        {currentRound && (
          <PreflopRound
            round={currentRound}
            roundNumber={roundNumber}
            totalRounds={totalRounds}
            score={score}
            streak={streak}
            onAnswer={() => {}}
            disabled
          />
        )}
        <RoundFeedback
          key={roundNumber}
          result={lastResult}
          language={i18n.language}
          onContinue={handleContinueAfterFeedback}
        />
      </>
    );
  }

  if (phase === "results" && finalResults) {
    return (
      <SessionResults
        score={finalResults.score}
        correctCount={finalResults.correct}
        totalRounds={finalResults.total}
        bestStreak={finalResults.bestStreak}
        chipsEarned={finalResults.chipsEarned}
        difficulty="beginner"
        rounds={finalResults.rounds}
        language={i18n.language}
        onPlayAgain={handlePlayAgain}
      />
    );
  }

  return null;
};
